import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="text-xl font-bold text-foreground">
              VibeSprint
            </a>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">
          Last updated: January 2026
        </p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Welcome to VibeSprint. By accessing or using our AI Product Decision Engine 
              service, you agree to be bound by these Terms of Service ("Terms"). If you 
              do not agree to these Terms, please do not use our service.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              These Terms constitute a legally binding agreement between you and VibeSprint 
              ("we", "us", or "our"). We reserve the right to modify these Terms at any time. 
              Your continued use of the service after changes constitutes acceptance of the 
              modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              VibeSprint is an AI-powered product decision engine that helps founders and 
              product teams analyze user feedback and prioritize product decisions. Our 
              service includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>AI-powered feedback analysis and categorization</li>
              <li>Revenue impact estimation and ROI calculations</li>
              <li>Product decision recommendations (Build Next, Fix Now, Ignore)</li>
              <li>Roadmap and changelog management</li>
              <li>Trend analytics and insights</li>
              <li>Feedback import and processing tools</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. User Accounts and Registration</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Current MVP:</strong> VibeSprint currently operates without per-user authentication.
              The service handles requests as a shared demo user.
              You can use the product without creating an account.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Future Authentication:</strong> When account creation becomes available:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You must be at least 18 years old to create an account</li>
              <li>One person or legal entity may maintain only one account</li>
              <li>You are responsible for all activities under your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Demo Data Disclaimer</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Important:</strong> VibeSprint's MVP includes simulated demo data 
              for demonstration purposes. This data is:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Fictional and generated for illustrative purposes only</li>
              <li>Not based on real user feedback or companies</li>
              <li>Provided "as-is" for evaluation of the product's capabilities</li>
              <li>Not intended for production decision-making</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              When importing your own feedback data, you are responsible for ensuring you 
              have the right to upload and process that data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Acceptable Use Policy</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You agree to use VibeSprint only for lawful purposes. You must not:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Reverse engineer, decompile, or disassemble the service</li>
              <li>Use the service to process illegal, defamatory, or infringing content</li>
              <li>Abuse, harass, or harm other users or our staff</li>
              <li>Scrape, harvest, or collect data from the service without permission</li>
              <li>Resell or redistribute the service without authorization</li>
              <li>Use the service to spam or send unsolicited communications</li>
              <li>Exceed rate limits or abuse API endpoints</li>
              <li>Impersonate another person or entity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. AI-Generated Content</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              VibeSprint uses an automated analysis engine to analyze 
              feedback and generate product recommendations. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>AI-generated insights are recommendations, not guarantees</li>
              <li>You are solely responsible for product decisions made based on our analysis</li>
              <li>AI outputs may occasionally be inaccurate or incomplete</li>
              <li>You should use human judgment when evaluating AI recommendations</li>
              <li>We do not guarantee specific business outcomes or revenue results</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Data Ownership and License</h2>
            <h3 className="text-xl font-semibold mb-3 mt-6">7.1 Your Data</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You retain all ownership rights to the feedback and data you upload to 
              VibeSprint. By using our service, you grant us a limited license to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Process and analyze your data using AI</li>
              <li>Store your data with a managed database provider</li>
              <li>Generate insights and recommendations based on your data</li>
              <li>Use anonymized, aggregated data for service improvement</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">7.2 Our Service</h3>
            <p className="text-muted-foreground leading-relaxed">
              VibeSprint and its original content, features, and functionality are owned 
              by us and are protected by international copyright, trademark, and other 
              intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Payment and Subscriptions</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Current MVP:</strong> VibeSprint is currently free to use during 
              the beta/MVP phase.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Future Paid Plans:</strong> When paid subscriptions are introduced:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Pricing will be clearly communicated before purchase</li>
              <li>Payments are processed through secure third-party providers (e.g., Stripe)</li>
              <li>Subscriptions renew automatically unless cancelled</li>
              <li>Refunds may be available according to our refund policy</li>
              <li>We reserve the right to modify pricing with 30 days' notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Service Availability</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We strive to provide reliable service, but we do not guarantee:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Uninterrupted or error-free operation</li>
              <li>Availability at all times (maintenance may be required)</li>
              <li>Compatibility with all devices or browsers</li>
              <li>Specific response times or performance metrics</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We reserve the right to modify, suspend, or discontinue the service (or any 
              part thereof) at any time with or without notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We may terminate or suspend your access immediately, without prior notice, for:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Non-payment (when applicable)</li>
              <li>Abuse of the service or staff</li>
              <li>Any other reason at our sole discretion</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Upon termination, your right to use the service will immediately cease. You 
              may request export of your data by contacting support@vibesprint.ai.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Disclaimers</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES 
              OF ANY KIND, EXPRESS OR IMPLIED.</strong>
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We disclaim all warranties, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Merchantability and fitness for a particular purpose</li>
              <li>Accuracy, reliability, or completeness of AI-generated insights</li>
              <li>Non-infringement of third-party rights</li>
              <li>Security or freedom from viruses or harmful components</li>
              <li>Specific business outcomes or revenue results</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, VIBESPRINT SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Business interruption or loss of goodwill</li>
              <li>Decisions made based on AI-generated recommendations</li>
              <li>Unauthorized access to or alteration of your data</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Our total liability shall not exceed the amount you paid (if any) for the 
              service in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless VibeSprint and its affiliates from 
              any claims, damages, losses, or expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Your use of the service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Content you upload or process through the service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use third-party AI and email service providers to process data on our behalf.
              Our service also integrates with other third-party services (e.g.,
              managed database and analytics providers). We are not responsible for the 
              availability, accuracy, or policies of these third-party services. Your use 
              of third-party services is subject to their respective terms and policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">15. Governing Law and Disputes</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with applicable 
              laws, without regard to conflict of law principles.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Any disputes arising from these Terms or the service shall first be resolved 
              through good-faith negotiation. If unresolved, disputes may be subject to 
              binding arbitration or court jurisdiction as applicable by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">16. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these Terms at any time. Material changes 
              will be notified via email (when available) or prominent notice on our 
              website. Your continued use after changes constitutes acceptance. If you 
              do not agree, you must stop using the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">17. Severability</h2>
            <p className="text-muted-foreground leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, 
              that provision shall be limited or eliminated to the minimum extent necessary, 
              and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">18. Entire Agreement</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement 
              between you and VibeSprint regarding the service and supersede all prior 
              agreements and understandings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">19. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-foreground"><strong>Email:</strong> support@vibesprint.ai</p>
              <p className="text-foreground mt-2"><strong>Service:</strong> VibeSprint - AI Product Decision Engine</p>
            </div>
          </section>

          <section className="mb-8">
            <p className="text-muted-foreground leading-relaxed italic">
              By using VibeSprint, you acknowledge that you have read, understood, and 
              agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 VibeSprint. Built for founders who ship.
            </p>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
                Privacy
              </a>
              <a href="/terms" className="text-sm text-muted-foreground hover:text-foreground">
                Terms
              </a>
              <a href="mailto:support@vibesprint.ai" className="text-sm text-muted-foreground hover:text-foreground">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
